import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FamilyTreeCanvas } from "./FamilyTreeCanvas";
import { FamilyCrestBuilder } from "./FamilyCrestBuilder";
import { FamilyTreeDesignModal } from "./FamilyTreeDesignModal";
import { PhotoCropModal } from "./PhotoCropModal";
import { getDesign } from "./designs";
import { SymbolThumb, CrestSVG } from "./CrestSVG";
import { SIGILS } from "./crestConfig";
import { serializeSvgWithFonts } from "./crestFonts";
import { buildFamilyLayout } from "./layout";
import "./family-tree.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = () =>
  axios.create({
    baseURL: `${BACKEND_URL}/api`,
    headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
  });

function photoSrc(url) {
  if (!url) return null;
  return url.startsWith("/api") ? `${BACKEND_URL}${url}` : url;
}

function initials(f, l) {
  return `${f?.[0] ?? ""}${l?.[0] ?? ""}`.toUpperCase();
}

function yearsLabel(m) {
  const b = m.birthDate ? new Date(m.birthDate).getFullYear() : "";
  const d = m.deathDate ? new Date(m.deathDate).getFullYear() : "";
  if (!b && !d) return "";
  return `${b || "?"}–${d || ""}`;
}

export default function FamilyTreeFeature() {
  const navigate = useNavigate();
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePanel, setActivePanel] = useState("tree");
  const [showDesign, setShowDesign] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [wizardFirst, setWizardFirst] = useState(false);
  const [crestDraft, setCrestDraft] = useState(null);
  const [linkables, setLinkables] = useState({ beneficiaries: [], whispers: [], albums: [] });

  useEffect(() => {
    api()
      .get("/family-tree")
      .then((r) => setTree(r.data))
      .catch(() => toast.error("Could not load your family tree."))
      .finally(() => setLoading(false));
    loadLinkables();
  }, []);

  function loadLinkables() {
    return api()
      .get("/family-tree/linkables")
      .then((r) => setLinkables(r.data))
      .catch(() => {});
  }

  function applyMember(updated) {
    setTree((t) => ({ ...t, members: t.members.map((m) => (m.id === updated.id ? updated : m)) }));
  }

  async function addLink(memberId, type, itemId) {
    if (!itemId) return;
    try {
      const r = await api().post(`/family-tree/members/${memberId}/links`, { type, item_id: itemId });
      applyMember(r.data);
      toast.success("Linked");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not link this item.");
    }
  }

  async function removeLink(memberId, type, itemId) {
    try {
      const r = await api().delete(`/family-tree/members/${memberId}/links/${type}/${itemId}`);
      applyMember(r.data);
    } catch (e) {
      toast.error("Could not remove this link.");
    }
  }

  async function createBeneficiaryFromMember(memberId, payload) {
    try {
      const r = await api().post(`/family-tree/members/${memberId}/create-beneficiary`, payload);
      applyMember(r.data);
      await loadLinkables();
      toast.success("Beneficiary invitation sent 💜");
      return true;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((d && d.message) || (typeof d === "string" ? d : "") || "Could not create the beneficiary.");
      return false;
    }
  }

  async function uploadMemberPhoto(memberId, file, onProgress) {
    const form = new FormData();
    form.append("file", file);
    const r = await axios.post(`${BACKEND_URL}/api/family-tree/members/${memberId}/photo`, form, {
      headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
      onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded / e.total) * 100)),
    });
    applyMember(r.data);
    return r.data;
  }

  async function removeMemberPhoto(memberId) {
    const r = await api().delete(`/family-tree/members/${memberId}/photo`);
    applyMember(r.data);
    return r.data;
  }

  const selectedMember = useMemo(
    () => tree?.members.find((m) => m.id === selectedMemberId),
    [tree, selectedMemberId]
  );

  async function addMember(member, relation, opts = {}) {
    try {
      const r = await api().post("/family-tree/members", { ...member, relation: relation || null });
      setTree(r.data.tree);
      if (!opts.keepOpen) {
        setSelectedMemberId(r.data.member_id);
        setActivePanel("tree");
      }
      toast.success(`${member.firstName} added to your family tree 🌳`);
      return r.data.member_id;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add this person.");
      return null;
    }
  }

  async function updateMember(updated) {
    try {
      const { id, ...fields } = updated;
      const r = await api().put(`/family-tree/members/${id}`, fields);
      setTree((t) => ({ ...t, members: t.members.map((m) => (m.id === id ? r.data : m)) }));
      toast.success("Changes saved");
    } catch (e) {
      toast.error("Could not save changes.");
    }
  }

  async function deleteMember(memberId) {
    try {
      await api().delete(`/family-tree/members/${memberId}`);
      setTree((t) => ({
        ...t,
        members: t.members.filter((m) => m.id !== memberId),
        relationships: t.relationships.filter((r) => r.personAId !== memberId && r.personBId !== memberId),
      }));
      setSelectedMemberId(null);
      toast.success("Person removed");
    } catch (e) {
      toast.error("Could not remove this person.");
    }
  }

  async function saveName(name) {
    if (!name.trim() || name === tree.name) return;
    try {
      const r = await api().put("/family-tree", { name });
      setTree(r.data);
    } catch (e) {
      toast.error("Could not rename the tree.");
    }
  }

  async function chooseTheme(key) {
    setTree((t) => ({ ...t, theme: key }));
    try {
      await api().put("/family-tree", { theme: key });
      toast.success(`${getDesign(key).name} applied`);
    } catch (e) {
      toast.error("Could not update the design.");
    }
  }

  async function saveCrest() {
    try {
      const r = await api().put("/family-tree", { crest: crestDraft });
      setTree(r.data);
      setCrestDraft(null);
      setActivePanel("tree");
      toast.success("Family crest saved");
    } catch (e) {
      toast.error("Could not save the crest.");
    }
  }

  async function addRelationship(subjectId, otherId, type) {
    try {
      const r = await api().post("/family-tree/relationships", { subjectId, otherId, type });
      setTree(r.data);
      toast.success("Relationship added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add this relationship.");
    }
  }

  async function removeRelationship(relationshipId) {
    try {
      const r = await api().delete(`/family-tree/relationships/${relationshipId}`);
      setTree(r.data);
      toast.success("Relationship removed");
    } catch (e) {
      toast.error("Could not remove this relationship.");
    }
  }

  async function enableShare() {
    try {
      const r = await api().post("/family-tree/share");
      setTree((t) => ({ ...t, shareEnabled: true, shareToken: r.data.shareToken }));
      toast.success("View-only link is live");
    } catch (e) {
      toast.error("Could not create the share link.");
    }
  }

  async function disableShare() {
    try {
      await api().delete("/family-tree/share");
      setTree((t) => ({ ...t, shareEnabled: false, shareToken: null }));
      toast.success("Sharing turned off");
    } catch (e) {
      toast.error("Could not disable sharing.");
    }  }

  async function ensureCrestRender() {
    // Rasterize the current crest and upload it so the keepsake/PDF always has an up-to-date image.
    const svg = document.querySelector("#keepsake-crest-src svg");
    if (!svg) return;
    try {
      const data = await serializeSvgWithFonts(svg);
      const url = URL.createObjectURL(new Blob([data], { type: "image/svg+xml;charset=utf-8" }));
      const canvas = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const c = document.createElement("canvas");
          c.width = 780; c.height = 1020;
          const ctx = c.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, c.width, c.height);
          ctx.drawImage(img, 0, 0, c.width, c.height);
          URL.revokeObjectURL(url);
          resolve(c);
        };
        img.onerror = reject;
        img.src = url;
      });
      const blob = await new Promise((r) => canvas.toBlob(r, "image/png", 0.92));
      if (!blob) return;
      const form = new FormData();
      form.append("file", blob, "rendered.png");
      await api().post("/family-tree/crest/render", form, { headers: { "Content-Type": "multipart/form-data" } });
    } catch (e) {
      /* non-fatal — keepsake will still render without the crest */
    }
  }

  async function downloadKeepsakeBook() {
    try {
      toast.message("Preparing your keepsake book…");
      await ensureCrestRender();
      const r = await api().get("/family-tree/keepsake-book", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(tree.crest?.familyName || tree.name || "Family").replace(/\s+/g, "-")}-Keepsake-Book.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not generate the keepsake book.");
    }
  }

  if (loading || !tree) {
    return (
      <main className="family-tree-feature">
        <div style={{ display: "grid", placeItems: "center", minHeight: "60vh", color: "#5f6772" }}>
          Loading your family tree…
        </div>
      </main>
    );
  }

  const design = getDesign(tree.theme);

  return (
    <main className="family-tree-feature">
      <header className="family-tree-header">
        <div>
          <button className="ghost-btn" onClick={() => navigate("/dashboard")} data-testid="family-tree-back">
            ← Dashboard
          </button>
          <span className="eyebrow">EasyPassing Family Legacy</span>
          <input
            className="tree-title-input"
            defaultValue={tree.name}
            onBlur={(e) => saveName(e.target.value)}
            aria-label="Family tree name"
            data-testid="family-tree-name"
          />
        </div>

        <nav className="tree-actions" aria-label="Family tree controls">
          <button onClick={() => setActivePanel("member")} data-testid="add-person-btn">+ Add Person</button>
          <button
            onClick={() => setShowDesign(true)}
            data-testid="choose-tree-btn"
            className="design-pill"
            aria-label={`Tree design: ${design.name} — tap to change`}
            title={`Tree design: ${design.name}`}
          >
            <span className="design-pill-thumb" style={{ backgroundImage: `url(${design.treeIcon || design.treeImage})` }} />
            <span className="design-pill-label">Tree Style</span>
            <span className="design-pill-caret" aria-hidden="true">▾</span>
          </button>
          <button onClick={() => navigate("/family-tree/crest")} data-testid="design-crest-btn">Design Crest</button>
          <button className="secondary" onClick={() => setShowShare(true)} data-testid="share-tree-btn">Share</button>
          <button className="secondary" onClick={downloadKeepsakeBook} data-testid="keepsake-book-btn">Keepsake Book</button>
          <button className="secondary" onClick={() => window.print()} data-testid="print-tree-btn">Print Keepsake</button>
        </nav>
      </header>

      <FamilyTreeDesignModal
        open={showDesign}
        value={tree.theme}
        onSelect={chooseTheme}
        onClose={() => setShowDesign(false)}
      />

      {/* Hidden, offscreen crest used to rasterize a fresh PNG for the keepsake book / PDF exports */}
      <div
        id="keepsake-crest-src"
        aria-hidden="true"
        style={{ position: "absolute", left: -99999, top: 0, width: 520, height: 680, pointerEvents: "none", opacity: 0 }}
      >
        <CrestSVG crest={tree.crest} size={520} />
      </div>

      <ShareModal
        open={showShare}
        tree={tree}
        onEnable={enableShare}
        onDisable={disableShare}
        onClose={() => setShowShare(false)}
      />

      {activePanel === "crest" && crestDraft && (
        <section className="panel">
          <div className="panel-heading">
            <div>
              <h2>Family Crest Studio</h2>
              <p>Create a crest that appears on your family tree.</p>
            </div>
            <button onClick={saveCrest} data-testid="crest-done-btn">Done</button>
          </div>

          <FamilyCrestBuilder value={crestDraft} onChange={(crest) => setCrestDraft(crest)} />
        </section>
      )}

      {activePanel === "member" && (
        <AddRelativeWizard
          existingMembers={tree.members}
          isFirstPerson={wizardFirst || tree.members.length === 0}
          onCancel={() => { setWizardFirst(false); setActivePanel("tree"); }}
          onSave={addMember}
          onAddPhoto={(id) => { setWizardFirst(false); setSelectedMemberId(id); setActivePanel("tree"); }}
        />
      )}

      {activePanel !== "member" && activePanel !== "crest" && (
        <>
          {tree.members.length === 0 ? (
            <FamilyTreeEmptyState
              design={design}
              onStartWithMe={() => { setWizardFirst(true); setActivePanel("member"); }}
              onInvite={() => setShowShare(true)}
            />
          ) : (
            <>
              <ReminderBanner members={tree.members} onOpen={(id) => setSelectedMemberId(id)} />

              <FamilyTreeCanvas
                tree={tree}
                focusMemberId={selectedMemberId}
                onSelectMember={(id) => setSelectedMemberId(id)}
                onEditCrest={() => navigate("/family-tree/crest")}
              />

              <button
                className="ft-fab"
                onClick={() => { setWizardFirst(false); setActivePanel("member"); }}
                data-testid="add-person-fab"
                aria-label="Add relative"
              >
                +
              </button>
            </>
          )}

          {selectedMember && (
            <MemberEditor
              key={selectedMember.id}
              member={selectedMember}
              linkables={linkables}
              allMembers={tree.members}
              relationships={tree.relationships}
              onClose={() => setSelectedMemberId(null)}
              onSave={updateMember}
              onDelete={deleteMember}
              onAddLink={addLink}
              onRemoveLink={removeLink}
              onCreateBeneficiary={createBeneficiaryFromMember}
              onUploadPhoto={uploadMemberPhoto}
              onRemovePhoto={removeMemberPhoto}
              onAddRelationship={addRelationship}
              onRemoveRelationship={removeRelationship}
              onOpenLinked={(type) => navigate(type === "album" ? "/albums" : type === "whisper" ? "/whispers" : "/benefactors")}
            />
          )}
        </>
      )}

      <KeepsakePrint tree={tree} />
    </main>
  );
}

function KeepsakePrint({ tree }) {
  const layout = useMemo(
    () => buildFamilyLayout(tree.members, tree.relationships),
    [tree.members, tree.relationships]
  );
  const generations = useMemo(() => {
    const map = new Map();
    layout.nodes.forEach((n) => map.set(n.generation, [...(map.get(n.generation) || []), n]));
    return [...map.entries()].sort(([a], [b]) => a - b);
  }, [layout.nodes]);

  return (
    <div className="ft-keepsake" aria-hidden="true">
      <div className="ft-keepsake-header">
        {tree.crest && tree.crest.crestPlacement !== "hidden" && (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
            <CrestSVG crest={tree.crest} size={120} />
          </div>
        )}
        <div className="ft-keepsake-title">{tree.name}</div>
        {tree.crest?.motto && <div className="ft-keepsake-motto">“{tree.crest.motto}”</div>}
      </div>
      {generations.map(([g, rows]) => (
        <div key={g} className="ft-keepsake-gen">
          <div className="ft-keepsake-gen-label">Generation {g + 1}</div>
          <div className="ft-keepsake-people">
            {rows.map((n) => {
              const src = photoSrc(n.photoUrl);
              return (
                <div key={n.id} className="ft-keepsake-person">
                  {src ? <img src={src} alt="" /> : <span className="ini">{initials(n.firstName, n.lastName)}</span>}
                  <span>
                    <b>{n.preferredName || n.firstName} {n.lastName}</b>
                    <small>{yearsLabel(n)}</small>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const WIZARD_TILES = [
  { key: "parent", label: "Parent", type: "parent", icon: "👤", needsRelated: true, hint: "Add someone's mother or father" },
  { key: "child", label: "Child", type: "child", icon: "🧒", needsRelated: true, hint: "Add someone's son or daughter" },
  { key: "spouse", label: "Spouse", type: "spouse", icon: "💍", needsRelated: true, hint: "Add a husband or wife" },
  { key: "partner", label: "Partner", type: "partner", icon: "❤", needsRelated: true, hint: "Add a life partner" },
  { key: "sibling", label: "Sibling", type: "sibling", icon: "👥", needsRelated: true, hint: "Add a brother or sister" },
  { key: "adoptive_child", label: "Adoptive", type: "adoptive_child", icon: "🤝", needsRelated: true, hint: "Adoptive parent or child" },
  { key: "step_child", label: "Step-relative", type: "step_child", icon: "🪜", needsRelated: true, hint: "Step-parent or step-child" },
  { key: "chosen_family", label: "Chosen family", type: "chosen_family", icon: "🌟", needsRelated: true, hint: "Someone dear who is family by choice" },
];

function AddRelativeWizard({ existingMembers, isFirstPerson, onCancel, onSave, onAddPhoto }) {
  const hasExisting = existingMembers.length > 0;
  // For the very first person there is no relationship step.
  const [step, setStep] = useState(isFirstPerson || !hasExisting ? 3 : 1);
  const [tile, setTile] = useState(null);
  const [relType, setRelType] = useState(null);
  const [relatedToId, setRelatedToId] = useState(existingMembers[0]?.id ?? "");
  const [search, setSearch] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isLiving, setIsLiving] = useState(true);
  const [birthDate, setBirthDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [addedId, setAddedId] = useState(null);
  const [addedName, setAddedName] = useState("");

  const filtered = existingMembers.filter((m) => {
    const n = `${m.firstName} ${m.lastName} ${m.preferredName || ""}`.toLowerCase();
    return n.includes(search.trim().toLowerCase());
  });

  function pickTile(t) {
    setTile(t.key);
    setRelType(t.type);
    // if only one existing person, skip the "related to" step
    if (existingMembers.length <= 1) {
      setRelatedToId(existingMembers[0]?.id ?? "");
      setStep(3);
    } else {
      setStep(2);
    }
  }

  async function submit() {
    if (!firstName.trim() || !lastName.trim() || busy) return;
    setBusy(true);
    const relation =
      !isFirstPerson && relType && relatedToId ? { relatedToId, type: relType } : undefined;
    const id = await onSave(
      {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        isLiving,
        birthDate: birthDate || undefined,
        privacyLevel: isLiving ? "family" : "family",
      },
      relation,
      { keepOpen: true }
    );
    setBusy(false);
    if (id) {
      setAddedId(id);
      setAddedName(firstName.trim());
      setStep(4);
    }
  }

  function resetForAnother() {
    setStep(hasExisting ? 1 : 3);
    setTile(null);
    setRelType(null);
    setFirstName("");
    setLastName("");
    setIsLiving(true);
    setBirthDate("");
    setAddedId(null);
  }

  return (
    <section className="panel ft-wizard" data-testid="add-relative-wizard">
      <div className="panel-heading">
        <div>
          <h2>{isFirstPerson ? "Start with yourself" : "Add a relative"}</h2>
          <p>
            {step === 1 && "Who are you adding?"}
            {step === 2 && "Who are they related to?"}
            {step === 3 && "Just the basics — you can add more details later."}
            {step === 4 && "Nicely done!"}
          </p>
        </div>
        {step < 4 && (
          <div className="ft-wizard-steps" aria-hidden="true">
            {[1, 2, 3].map((s) => (
              <span key={s} className={`ft-wizard-dot ${step >= s ? "on" : ""}`} />
            ))}
          </div>
        )}
      </div>

      {/* STEP 1: relationship type */}
      {step === 1 && (
        <div className="ft-wizard-tiles" data-testid="wizard-step-relationship">
          {WIZARD_TILES.map((t) => (
            <button
              key={t.key}
              type="button"
              className="ft-wizard-tile"
              onClick={() => pickTile(t)}
              data-testid={`wizard-tile-${t.key}`}
            >
              <span className="ft-wizard-tile-icon" aria-hidden="true">{t.icon}</span>
              <span className="ft-wizard-tile-label">{t.label}</span>
              <span className="ft-wizard-tile-hint">{t.hint}</span>
            </button>
          ))}
        </div>
      )}

      {/* STEP 2: related person */}
      {step === 2 && (
        <div data-testid="wizard-step-related">
          <input
            className="ft-wizard-search"
            placeholder="Search family members…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="wizard-related-search"
          />
          <div className="ft-wizard-people">
            {filtered.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`ft-wizard-person ${relatedToId === m.id ? "on" : ""}`}
                onClick={() => setRelatedToId(m.id)}
                data-testid={`wizard-person-${m.id}`}
              >
                <span className="ft-wizard-person-avatar">
                  {m.photoUrl ? <img src={m.photoUrl} alt="" /> : (m.firstName?.[0] || "?")}
                </span>
                <span>{m.preferredName || m.firstName} {m.lastName}</span>
              </button>
            ))}
            {filtered.length === 0 && <p className="ft-wizard-empty">No matches.</p>}
          </div>
          <div className="form-actions">
            <button type="button" className="secondary" onClick={() => setStep(1)} data-testid="wizard-back-1">Back</button>
            <button type="button" onClick={() => setStep(3)} disabled={!relatedToId} data-testid="wizard-next-3">Continue</button>
          </div>
        </div>
      )}

      {/* STEP 3: basics */}
      {step === 3 && (
        <form
          className="member-form"
          onSubmit={(e) => { e.preventDefault(); submit(); }}
          data-testid="wizard-step-basics"
        >
          <label>
            First name
            <input required value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="member-first-name" />
          </label>
          <label>
            Last name
            <input required value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="member-last-name" />
          </label>
          <fieldset className="ft-wizard-living">
            <legend>Living or deceased</legend>
            <label className="ft-radio">
              <input type="radio" checked={isLiving} onChange={() => setIsLiving(true)} data-testid="wizard-living" /> Living
            </label>
            <label className="ft-radio">
              <input type="radio" checked={!isLiving} onChange={() => setIsLiving(false)} data-testid="wizard-deceased" /> Deceased
            </label>
          </fieldset>
          <label>
            Birth date <span className="ft-optional">(optional)</span>
            <input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} data-testid="wizard-birthdate" />
          </label>
          {isLiving && (
            <p className="ft-privacy-note" data-testid="wizard-privacy-note">
              🔒 Living people are visible only to approved family members by default.
            </p>
          )}
          <div className="form-actions">
            <button
              type="button"
              className="secondary"
              onClick={() => (isFirstPerson || !hasExisting ? onCancel() : setStep(existingMembers.length <= 1 ? 1 : 2))}
              data-testid="member-cancel"
            >
              {isFirstPerson || !hasExisting ? "Cancel" : "Back"}
            </button>
            <button type="submit" disabled={busy} data-testid="member-save">
              {busy ? "Adding…" : "Add to Family Tree"}
            </button>
          </div>
        </form>
      )}

      {/* STEP 4: success */}
      {step === 4 && (
        <div className="ft-wizard-success" data-testid="wizard-success">
          <div className="ft-wizard-success-check" aria-hidden="true">✓</div>
          <p><strong>{addedName}</strong> was added. Add their photo or another relative?</p>
          <div className="ft-wizard-success-actions">
            <button type="button" onClick={() => { onAddPhoto(addedId); }} data-testid="wizard-add-photo">Add their photo</button>
            <button type="button" className="secondary" onClick={resetForAnother} data-testid="wizard-add-another">Add another relative</button>
            <button type="button" className="secondary" onClick={onCancel} data-testid="wizard-done">Done</button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="form-actions">
          <button type="button" className="secondary" onClick={onCancel} data-testid="member-cancel">Cancel</button>
        </div>
      )}
    </section>
  );
}

function MemberPhotoUpload({ member, onUploadPhoto, onRemovePhoto }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);

  const src = photoSrc(member.photoUrl);

  function pickFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Please choose a JPG, PNG, or WEBP image.");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Image is too large (max 12MB).");
      return;
    }
    setCropSrc(URL.createObjectURL(file));
  }

  async function handleCropped(blob) {
    setCropSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    const file = new File([blob], "portrait.jpg", { type: "image/jpeg" });
    setUploading(true);
    setProgress(0);
    try {
      await onUploadPhoto(member.id, file, setProgress);
      toast.success("Photo updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not upload the photo.");
    } finally {
      setUploading(false);
    }
  }

  async function handleRemove() {
    setBusy(true);
    try {
      await onRemovePhoto(member.id);
      toast.success("Photo removed");
    } catch (err) {
      toast.error("Could not remove the photo.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ft-photo-uploader" data-testid="member-photo-uploader">
      {cropSrc && (
        <PhotoCropModal
          imageSrc={cropSrc}
          onCancel={() => setCropSrc((p) => { if (p) URL.revokeObjectURL(p); return null; })}
          onConfirm={handleCropped}
        />
      )}

      <div className="ft-photo-preview">
        {src ? (
          <img src={src} alt={`${member.firstName} ${member.lastName}`} data-testid="member-photo-preview" />
        ) : (
          <span className="ft-photo-placeholder" aria-hidden="true">
            {initials(member.firstName, member.lastName)}
          </span>
        )}
      </div>

      <div className="ft-photo-actions">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={pickFile}
          style={{ display: "none" }}
          data-testid="member-photo-input"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="member-photo-upload-btn"
        >
          {uploading ? `Uploading ${progress}%` : src ? "Replace photo" : "＋ Add photo"}
        </button>
        {src && !uploading && (
          <button type="button" className="secondary" onClick={handleRemove} disabled={busy} data-testid="member-photo-remove-btn">
            Remove
          </button>
        )}
        {uploading && (
          <div className="ft-progress" aria-label="Upload progress">
            <span style={{ width: `${progress}%` }} />
          </div>
        )}
        <small className="ft-photo-hint">JPG, PNG or WEBP. Drag & zoom to frame it.</small>
      </div>
    </div>
  );
}

const MEMORY_GROUPS = [
  { type: "whisper", key: "whispers", title: "Whispers", icon: "✉", color: "#6b3fa0" },
  { type: "album", key: "albums", title: "Albums", icon: "▦", color: "#b57a22" },
  { type: "beneficiary", key: "beneficiaries", title: "Beneficiaries", icon: "♥", color: "#a3436b" },
];

function LivingMemories({ member, linkables, onOpenLinked }) {
  const linked = member.links || { beneficiaries: [], whispers: [], albums: [] };
  const labelFor = (key, id) => (linkables[key] || []).find((x) => x.id === id)?.label || "Linked item";
  const total = MEMORY_GROUPS.reduce((n, g) => n + (linked[g.key] || []).length, 0);

  return (
    <section className="ft-memories" data-testid="living-memories">
      <div>
        <div className="ft-memories-group-title">Living Memories</div>
        {total === 0 && (
          <p className="ft-memory-empty">
            No memories linked yet. Use “Edit details → Connections” to link Whispers, Albums, or Beneficiaries.
          </p>
        )}
      </div>

      {MEMORY_GROUPS.map((g) => {
        const ids = linked[g.key] || [];
        if (ids.length === 0) return null;
        return (
          <div key={g.key} data-testid={`memories-${g.key}`}>
            <div className="ft-memories-group-title">{g.title}</div>
            <div style={{ display: "grid", gap: 8 }}>
              {ids.map((id) => (
                <button
                  key={id}
                  type="button"
                  className="ft-memory-item"
                  onClick={() => onOpenLinked(g.type, id)}
                  data-testid={`open-${g.key}-${id}`}
                >
                  <span className="ft-memory-icon" style={{ background: g.color }} aria-hidden="true">{g.icon}</span>
                  <span>
                    {labelFor(g.key, id)}
                    <small>Open {g.title.toLowerCase().replace(/s$/, "")}</small>
                  </span>
                  <span className="ft-memory-arrow" aria-hidden="true">→</span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </section>
  );
}

function FamilyTreeEmptyState({ design, onStartWithMe, onInvite }) {
  return (
    <div className="ft-empty-state" data-testid="family-tree-empty-state">
      <div
        className="ft-empty-illustration"
        style={{ backgroundImage: `url(${design.treeImage})` }}
        aria-hidden="true"
      >
        <span className="ft-empty-example-tag">Example</span>
      </div>
      <div className="ft-empty-card">
        <h2>Start your family tree</h2>
        <p>
          Begin with yourself, then add parents, children, siblings, or other important people.
          Your tree grows and rearranges itself automatically as you add each person.
        </p>
        <div className="ft-empty-actions">
          <button className="ft-empty-primary" onClick={onStartWithMe} data-testid="empty-start-with-me">
            Start With Me
          </button>
          <button className="ft-empty-secondary" onClick={onInvite} data-testid="empty-invite-relative">
            Invite a Relative to Help
          </button>
        </div>
      </div>
    </div>
  );
}

function MemberEditor({ member, linkables, allMembers, relationships, onClose, onSave, onDelete, onAddLink, onRemoveLink, onCreateBeneficiary, onUploadPhoto, onRemovePhoto, onAddRelationship, onRemoveRelationship, onOpenLinked }) {
  const [mode, setMode] = useState("profile");
  const [draft, setDraft] = useState(member);

  // Only reset to profile view when a DIFFERENT member is opened — not on every
  // in-place update (photo upload, save, link) which would kick the user out of edit mode.
  useEffect(() => {
    setDraft(member);
    setMode("profile");
  }, [member.id]);

  useEffect(() => {
    setDraft(member);
  }, [member]);

  const src = photoSrc(member.photoUrl);

  const displayName = `${member.preferredName || member.firstName} ${member.lastName}`.trim();
  function handleDelete() {
    if (window.confirm(`Remove ${displayName} from the family tree?\n\nThis also removes their relationships and can't be undone.`)) {
      onDelete(member.id);
    }
  }

  function saveChanges() {
    const living = draft.isLiving !== false;
    onSave({
      id: member.id,
      preferredName: draft.preferredName ?? "",
      biography: draft.biography ?? "",
      lifeStory: draft.lifeStory ?? "",
      favoriteQuote: draft.favoriteQuote ?? "",
      sigil: draft.sigil ?? "",
      privacyLevel: draft.privacyLevel,
      isLiving: living,
      birthDate: draft.birthDate ?? "",
      deathDate: living ? "" : (draft.deathDate ?? ""),
    });
  }

  return (
    <aside className="member-drawer" data-testid="member-drawer">
      <button className="drawer-close" onClick={onClose} aria-label="Close" data-testid="member-drawer-close">
        ×
      </button>

      {mode === "profile" ? (
        <>
          <div className="ft-profile-head" data-testid="member-profile">
            <div className="ft-profile-portrait">
              {src ? <img src={src} alt={`${member.firstName} ${member.lastName}`} /> : <span>{initials(member.firstName, member.lastName)}</span>}
            </div>
            <div className="ft-profile-name">{member.preferredName || member.firstName} {member.lastName}</div>
            {yearsLabel(member) && <div className="ft-profile-years">{yearsLabel(member)}</div>}
          </div>

          {member.favoriteQuote && (
            <p className="ft-quote" data-testid="profile-quote">“{member.favoriteQuote}”</p>
          )}

          {member.lifeStory && (
            <div className="ft-story-block" data-testid="profile-life-story">
              <span className="ft-story-label">Life story</span>
              <p className="ft-profile-bio">{member.lifeStory}</p>
            </div>
          )}

          {member.biography && <p className="ft-profile-bio">{member.biography}</p>}

          <LivingMemories member={member} linkables={linkables} onOpenLinked={onOpenLinked} />

          <div className="ft-profile-actions">
            <button onClick={() => setMode("edit")} data-testid="edit-details-btn">Edit details</button>
            <button className="secondary" onClick={onClose} data-testid="profile-close-btn">Close</button>
            <button className="danger" onClick={handleDelete} data-testid="profile-delete-btn">Remove from tree</button>
          </div>
        </>
      ) : (
        <>
          <div className="panel-heading" style={{ alignItems: "center" }}>
            <h2 style={{ fontSize: 22 }}>Edit {member.firstName}</h2>
            <button className="secondary" onClick={() => setMode("profile")} data-testid="back-to-profile-btn">← Back</button>
          </div>

          <MemberPhotoUpload member={member} onUploadPhoto={onUploadPhoto} onRemovePhoto={onRemovePhoto} />

          <label>
            Preferred name
            <input value={draft.preferredName ?? ""} onChange={(e) => setDraft({ ...draft, preferredName: e.target.value })} />
          </label>

          <fieldset className="ft-wizard-living" data-testid="edit-living-status">
            <legend>Living or deceased</legend>
            <label className="ft-radio">
              <input type="radio" checked={draft.isLiving !== false}
                onChange={() => setDraft({ ...draft, isLiving: true, deathDate: "" })} data-testid="edit-living" /> Living
            </label>
            <label className="ft-radio">
              <input type="radio" checked={draft.isLiving === false}
                onChange={() => setDraft({ ...draft, isLiving: false })} data-testid="edit-deceased" /> Deceased
            </label>
          </fieldset>

          <div className="ft-date-row">
            <label>
              Birth date
              <input type="date" value={draft.birthDate ?? ""}
                onChange={(e) => setDraft({ ...draft, birthDate: e.target.value })} data-testid="edit-birthdate" />
            </label>
            {draft.isLiving === false && (
              <label>
                Death date
                <input type="date" value={draft.deathDate ?? ""}
                  onChange={(e) => setDraft({ ...draft, deathDate: e.target.value })} data-testid="edit-deathdate" />
              </label>
            )}
          </div>

          <label>
            Favorite quote
            <input
              value={draft.favoriteQuote ?? ""}
              onChange={(e) => setDraft({ ...draft, favoriteQuote: e.target.value })}
              placeholder="A saying they were known for…"
              data-testid="member-favorite-quote"
            />
          </label>

          <label>
            Life story
            <textarea
              rows={5}
              value={draft.lifeStory ?? ""}
              onChange={(e) => setDraft({ ...draft, lifeStory: e.target.value })}
              placeholder="A short story of their life…"
              data-testid="member-life-story"
            />
          </label>

          <label>
            Biography (short note)
            <textarea rows={3} value={draft.biography ?? ""} onChange={(e) => setDraft({ ...draft, biography: e.target.value })} data-testid="member-biography" />
          </label>

          <label>
            Privacy
            <select value={draft.privacyLevel} onChange={(e) => setDraft({ ...draft, privacyLevel: e.target.value })}>
              <option value="private">Private</option>
              <option value="family">Family only</option>
              <option value="shared">Shared</option>
            </select>
          </label>

          <div data-testid="member-sigil-picker">
            <span style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Family sigil (personal emblem)</span>
            <div className="ft-sigil-picker">
              {SIGILS.map((s) => (
                <button
                  type="button"
                  key={s.id || "none"}
                  className={`ft-sigil-opt ${(draft.sigil || "") === s.id ? "active" : ""}`}
                  title={s.label}
                  onClick={() => setDraft({ ...draft, sigil: s.id })}
                  data-testid={`sigil-opt-${s.id || "none"}`}
                >
                  {s.id ? <SymbolThumb symbol={s.id} color="#071B33" /> : <span className="ft-sigil-none">None</span>}
                </button>
              ))}
            </div>
          </div>

          <button onClick={saveChanges} data-testid="member-save-changes">Save Changes</button>

          <RelationshipEditor
            member={member}
            allMembers={allMembers}
            relationships={relationships}
            onAddRelationship={onAddRelationship}
            onRemoveRelationship={onRemoveRelationship}
          />

          <MemberConnections
            member={member}
            linkables={linkables}
            onAddLink={onAddLink}
            onRemoveLink={onRemoveLink}
            onCreateBeneficiary={onCreateBeneficiary}
          />

          <button className="danger" onClick={handleDelete} data-testid="member-delete">
            Remove Person
          </button>
        </>
      )}
    </aside>
  );
}

// How each stored relationship reads FROM the subject member's point of view.
const REL_LABELS = {
  parent: { asA: "parent of", asB: "child of" },
  child: { asA: "child of", asB: "parent of" },
  spouse: { asA: "spouse of", asB: "spouse of" },
  partner: { asA: "partner of", asB: "partner of" },
  sibling: { asA: "sibling of", asB: "sibling of" },
  adoptive_parent: { asA: "adoptive parent of", asB: "adopted child of" },
  adoptive_child: { asA: "adopted child of", asB: "adoptive parent of" },
  step_parent: { asA: "step-parent of", asB: "step-child of" },
  step_child: { asA: "step-child of", asB: "step-parent of" },
  guardian: { asA: "guardian of", asB: "ward of" },
  ward: { asA: "ward of", asB: "guardian of" },
  chosen_family: { asA: "chosen family of", asB: "chosen family of" },
};

const REL_ADD_OPTIONS = [
  { value: "child", label: "child of" },
  { value: "parent", label: "parent of" },
  { value: "spouse", label: "spouse of" },
  { value: "partner", label: "partner of" },
  { value: "sibling", label: "sibling of" },
  { value: "adoptive_child", label: "adopted child of" },
  { value: "adoptive_parent", label: "adoptive parent of" },
  { value: "step_child", label: "step-child of" },
  { value: "step_parent", label: "step-parent of" },
  { value: "guardian", label: "guardian of" },
  { value: "chosen_family", label: "chosen family of" },
];

function RelationshipEditor({ member, allMembers, relationships, onAddRelationship, onRemoveRelationship }) {
  const nameOf = (id) => {
    const m = (allMembers || []).find((x) => x.id === id);
    return m ? `${m.preferredName || m.firstName} ${m.lastName}` : "Someone";
  };
  const others = (allMembers || []).filter((m) => m.id !== member.id);
  const [type, setType] = useState("child");
  const [otherId, setOtherId] = useState(others[0]?.id ?? "");

  const mine = (relationships || []).filter((r) => r.personAId === member.id || r.personBId === member.id);

  return (
    <section className="ft-rel-editor" data-testid="relationship-editor">
      <strong style={{ display: "block", marginBottom: 4, color: "var(--ep-navy)" }}>Relationships</strong>
      <p style={{ fontSize: 13, color: "#5f6772", marginTop: 0, marginBottom: 6 }}>
        Fix or re-link who is whose parent, spouse, or child.
      </p>

      <div className="ft-rel-list">
        {mine.length === 0 && <span style={{ fontSize: 12, color: "#8b929c" }}>No relationships yet</span>}
        {mine.map((r) => {
          const isA = r.personAId === member.id;
          const otherPerson = isA ? r.personBId : r.personAId;
          const labelSet = REL_LABELS[r.relationshipType] || { asA: r.relationshipType, asB: r.relationshipType };
          const label = isA ? labelSet.asA : labelSet.asB;
          return (
            <div className="ft-rel-row" key={r.id} data-testid={`relationship-${r.id}`}>
              <span><b>{label}</b> {nameOf(otherPerson)}</span>
              <button
                type="button"
                className="ft-rel-remove"
                aria-label="Remove relationship"
                onClick={() => onRemoveRelationship(r.id)}
                data-testid={`remove-relationship-${r.id}`}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>

      {others.length > 0 && (
        <div className="ft-rel-add">
          <label>
            Is a…
            <select value={type} onChange={(e) => setType(e.target.value)} data-testid="relationship-type">
              {REL_ADD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label>
            …of
            <select value={otherId} onChange={(e) => setOtherId(e.target.value)} data-testid="relationship-other">
              {others.map((m) => (
                <option key={m.id} value={m.id}>{m.preferredName || m.firstName} {m.lastName}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="ft-rel-add-btn"
            onClick={() => otherId && onAddRelationship(member.id, otherId, type)}
            data-testid="add-relationship-btn"
          >
            + Add relationship
          </button>
        </div>
      )}
    </section>
  );
}

function ReminderBanner({ members, onOpen }) {
  const now = new Date();
  const items = [];
  (members || []).forEach((m) => {
    [["birthday", m.birthDate], ["remembrance", m.deathDate]].forEach(([kind, dateStr]) => {
      if (!dateStr) return;
      const d = new Date(dateStr);
      if (isNaN(d)) return;
      const next = new Date(now.getFullYear(), d.getMonth(), d.getDate());
      let diff = Math.round((next - new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
      if (diff < 0) diff += 365;
      if (diff <= 14) items.push({ id: m.id, name: m.preferredName || m.firstName, kind, diff });
    });
  });
  if (items.length === 0) return null;
  items.sort((a, b) => a.diff - b.diff);

  const when = (diff) => (diff === 0 ? "today" : diff === 1 ? "tomorrow" : `in ${diff} days`);

  return (
    <div className="ft-reminder-banner" data-testid="reminder-banner">
      <span className="ft-reminder-icon" aria-hidden="true">🌟</span>
      <span>Upcoming family days:</span>
      {items.slice(0, 6).map((it, i) => (
        <button
          key={`${it.id}-${it.kind}-${i}`}
          type="button"
          className={`ft-reminder-chip ${it.kind}`}
          onClick={() => onOpen(it.id)}
          data-testid={`reminder-${it.id}-${it.kind}`}
        >
          {it.kind === "birthday" ? "🎂" : "🕊"} {it.name}'s {it.kind === "birthday" ? "birthday" : "remembrance day"} {when(it.diff)}
        </button>
      ))}
    </div>
  );
}

function ShareModal({ open, tree, onEnable, onDisable, onClose }) {
  if (!open) return null;
  const link = tree.shareToken ? `${window.location.origin}/family-tree/shared/${tree.shareToken}` : "";

  return (
    <div className="ft-design-backdrop" onClick={onClose} data-testid="share-modal-backdrop">
      <div className="ft-design-modal ft-share-modal" role="dialog" aria-modal="true" aria-label="Share family tree" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
        <div className="ft-design-modal-head">
          <div>
            <span className="eyebrow">Share</span>
            <h2>View-only family tree link</h2>
            <p>Relatives can explore the tree — they can’t edit anything.</p>
          </div>
          <button className="ft-design-close" onClick={onClose} aria-label="Close" data-testid="share-modal-close">×</button>
        </div>

        <div className="ft-share-toggle">
          <span style={{ fontWeight: 700 }}>{tree.shareEnabled ? "Sharing is on" : "Sharing is off"}</span>
          {tree.shareEnabled ? (
            <button className="danger" onClick={onDisable} data-testid="share-disable-btn">Turn off</button>
          ) : (
            <button onClick={onEnable} data-testid="share-enable-btn">Create link</button>
          )}
        </div>

        {tree.shareEnabled && link && (
          <>
            <div className="ft-share-link-row">
              <input readOnly value={link} data-testid="share-link-input" onFocus={(e) => e.target.select()} />
              <button
                onClick={() => navigator.clipboard?.writeText(link).then(() => toast.success("Link copied"), () => {})}
                data-testid="share-copy-btn"
              >
                Copy
              </button>
            </div>
            <p className="ft-share-note">
              Anyone with this link can view the tree (private members are hidden). Turn it off any time to revoke access.
            </p>
          </>
        )}
      </div>
    </div>
  );
}


const LINK_GROUPS = [
  { type: "beneficiary", key: "beneficiaries", title: "Beneficiaries" },
  { type: "whisper", key: "whispers", title: "Whispers" },
  { type: "album", key: "albums", title: "Albums" },
];

function MemberConnections({ member, linkables, onAddLink, onRemoveLink, onCreateBeneficiary }) {
  const [showCreateBen, setShowCreateBen] = useState(false);
  const [benEmail, setBenEmail] = useState("");
  const [benRel, setBenRel] = useState("");
  const [benPrimary, setBenPrimary] = useState(false);
  const [creating, setCreating] = useState(false);

  const linkedIds = member.links || { beneficiaries: [], whispers: [], albums: [] };

  const labelFor = (key, id) => {
    const found = (linkables[key] || []).find((x) => x.id === id);
    return found ? found.label : "Linked item";
  };

  return (
    <section style={{ borderTop: "1px solid var(--ep-border)", paddingTop: 14 }} data-testid="member-connections">
      <strong style={{ display: "block", marginBottom: 8, color: "var(--ep-navy)" }}>Connections</strong>
      <p style={{ fontSize: 13, color: "#5f6772", marginTop: -4, marginBottom: 10 }}>
        Link this person to items in your plan (e.g. "also a beneficiary").
      </p>

      {LINK_GROUPS.map(({ type, key, title }) => {
        const linked = linkedIds[key] || [];
        const available = (linkables[key] || []).filter((x) => !linked.includes(x.id));
        return (
          <div key={key} style={{ marginBottom: 14 }} data-testid={`connections-${key}`}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{title}</div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
              {linked.length === 0 && <span style={{ fontSize: 12, color: "#8b929c" }}>None linked yet</span>}
              {linked.map((id) => (
                <span
                  key={id}
                  data-testid={`linked-${key}-${id}`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#eef1f5",
                           borderRadius: 999, padding: "5px 10px", fontSize: 12, fontWeight: 600 }}
                >
                  {labelFor(key, id)}
                  <button
                    type="button"
                    onClick={() => onRemoveLink(member.id, type, id)}
                    aria-label="Remove link"
                    data-testid={`unlink-${key}-${id}`}
                    style={{ background: "transparent", color: "#a32727", padding: 0, borderRadius: 0, fontSize: 14, lineHeight: 1 }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>

            {available.length > 0 && (
              <select
                value=""
                onChange={(e) => e.target.value && onAddLink(member.id, type, e.target.value)}
                data-testid={`add-link-${key}`}
              >
                <option value="">+ Link a {type}…</option>
                {available.map((x) => (
                  <option key={x.id} value={x.id}>{x.label}</option>
                ))}
              </select>
            )}
          </div>
        );
      })}

      {!showCreateBen ? (
        <button type="button" className="secondary" onClick={() => setShowCreateBen(true)} data-testid="create-beneficiary-toggle">
          + Create a beneficiary from this person
        </button>
      ) : (
        <div style={{ display: "grid", gap: 8, background: "#f6f8fa", padding: 12, borderRadius: 12 }} data-testid="create-beneficiary-form">
          <label>
            Their email
            <input type="email" value={benEmail} onChange={(e) => setBenEmail(e.target.value)} placeholder="name@example.com" data-testid="create-beneficiary-email" />
          </label>
          <label>
            Relationship (optional)
            <input value={benRel} onChange={(e) => setBenRel(e.target.value)} placeholder="e.g. daughter" />
          </label>
          <label style={{ flexDirection: "row", alignItems: "center", gap: 8, display: "flex", fontWeight: 600 }}>
            <input type="checkbox" style={{ width: "auto" }} checked={benPrimary} onChange={(e) => setBenPrimary(e.target.checked)} />
            Make primary executor
          </label>
          <div className="form-actions">
            <button type="button" className="secondary" onClick={() => setShowCreateBen(false)}>Cancel</button>
            <button
              type="button"
              disabled={creating || !benEmail.includes("@")}
              data-testid="create-beneficiary-submit"
              onClick={async () => {
                setCreating(true);
                const ok = await onCreateBeneficiary(member.id, { email: benEmail.trim(), relationship: benRel.trim() || undefined, is_primary: benPrimary });
                setCreating(false);
                if (ok) { setShowCreateBen(false); setBenEmail(""); setBenRel(""); setBenPrimary(false); }
              }}
            >
              {creating ? "Sending…" : "Create & Invite"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
